/*
 * Kivora Home — Buy 2, get the 3rd free: mix & match picker.
 * <kv-b2g1-builder> is rendered by sections/kv-b2g1-builder.liquid.
 *
 * Display only: the money logic is Shopify's automatic BXGY discount
 * ("Buy 2, get the 3rd free", collection buy-2-get-1, 100% off). For every
 * 3 qualifying items Shopify takes the cheapest ones off; this picker mirrors
 * that so the tray matches what the cart will show.
 */
(() => {
  if (customElements.get('kv-b2g1-builder')) return;

  const SET_SIZE = 3;
  const STORE_PREFIX = 'kv-b2g1:';
  const STORE_TTL = 3 * 24 * 60 * 60 * 1000;

  function withDelimiters(cents, precision, thousands, decimal) {
    const value = Number(cents);
    if (!isFinite(value)) return '0';
    const parts = (value / 100).toFixed(precision).split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
  }

  function formatMoney(cents, format) {
    const fmt = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    const token = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(token);
    let value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return fmt.replace(token, value);
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, (whole, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : whole
    );
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  class KvB2g1Builder extends HTMLElement {
    connectedCallback() {
      if (this.kvReady) return;
      const configNode = this.querySelector('[data-kv-config]');
      if (!configNode) return;
      try {
        this.cfg = JSON.parse(configNode.textContent);
      } catch (error) {
        console.error('[kv-b2g1] bad config', error);
        return;
      }
      this.kvReady = true;
      this.s = this.cfg.strings || {};
      this.maxPicks = Math.max(SET_SIZE, Number(this.cfg.maxPicks) || 12);
      this.storeKey = STORE_PREFIX + (this.cfg.sectionId || this.id);
      this.uidSeq = 0;
      this.busy = false;
      this.cartTotal = 0;
      this.cartQualifying = 0;
      this.filters = { band: 'all', room: 'all' };
      this.mobileQuery = window.matchMedia('(max-width: 989px)');

      this.products = new Map();
      this.variants = new Map();
      this.querySelectorAll('[data-kv-card]').forEach((card) => this.registerCard(card));

      this.tray = this.querySelector('[data-kv-tray]');
      this.panel = this.querySelector('[data-kv-panel]');
      this.trayTitle = this.tray && this.tray.querySelector('.kv-b2g1-tray__title');
      this.slotList = this.querySelector('[data-kv-slots]');
      this.msgEl = this.querySelector('[data-kv-msg]');
      this.totalsEl = this.querySelector('[data-kv-totals]');
      this.subtotalEl = this.querySelector('[data-kv-subtotal]');
      this.freeRow = this.querySelector('[data-kv-free-row]');
      this.freeLabel = this.querySelector('[data-kv-free-label]');
      this.savingEl = this.querySelector('[data-kv-saving]');
      this.totalEl = this.querySelector('[data-kv-total]');
      this.shipEl = this.querySelector('[data-kv-ship]');
      this.cartNoteEl = this.querySelector('[data-kv-cartnote]');
      this.mainEl = this.querySelector('.kv-b2g1__main');
      this.faqEl = this.querySelector('.kv-b2g1-faq');
      this.errorEl = this.querySelector('[data-kv-error]');
      this.toggleBtn = this.querySelector('[data-kv-action="toggle"]');
      this.progressEl = this.querySelector('[data-kv-progress]');
      this.miniTotalEl = this.querySelector('[data-kv-mini-total]');
      this.dots = Array.from(this.querySelectorAll('[data-kv-dots] > span'));
      this.ctaBtn = this.querySelector('[data-kv-cta]');
      this.ctaText = this.querySelector('[data-kv-cta-text]');
      this.liveEl = this.querySelector('[data-kv-live]');
      this.countEl = this.querySelector('[data-kv-count]');
      this.noResults = this.querySelector('[data-kv-noresults]');
      this.bar = this.querySelector('[data-kv-bar]');

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTrayFocusOut = this.onTrayFocusOut.bind(this);
      this.onQueryChange = this.onQueryChange.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
      if (this.tray) this.tray.addEventListener('focusout', this.onTrayFocusOut);
      if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', this.onQueryChange);

      // Keep the shipping hint and the "already in your cart" note in step with
      // changes made elsewhere (cart drawer, cart page, kv-cart.js gift add).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || event.source !== 'kv-b2g1-builder') this.refreshCartTotal();
        });
      }

      this.picks = this.restore();
      this.render();
      this.refreshCartTotal();
    }

    disconnectedCallback() {
      if (!this.kvReady) return;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.tray) this.tray.removeEventListener('focusout', this.onTrayFocusOut);
      if (this.mobileQuery.removeEventListener) this.mobileQuery.removeEventListener('change', this.onQueryChange);
      if (typeof this.cartUnsubscribe === 'function') this.cartUnsubscribe();
      this.cartUnsubscribe = null;
      window.clearTimeout(this.cartTimer);
      this.setBackgroundInert(false);
      this.kvReady = false;
    }

    registerCard(card) {
      const json = card.querySelector('[data-kv-product]');
      if (!json) return;
      let data;
      try {
        data = JSON.parse(json.textContent);
      } catch (error) {
        return;
      }
      if (!data || !Array.isArray(data.variants) || !data.variants.length) {
        card.hidden = true;
        return;
      }
      const entry = {
        id: String(data.id),
        data,
        card,
        select: card.querySelector('[data-kv-variant]'),
        pickBtn: card.querySelector('[data-kv-action="pick"]'),
        pickText: card.querySelector('[data-kv-pick-text]'),
        qtyEl: card.querySelector('[data-kv-qty]'),
        moreBtn: card.querySelector('[data-kv-action="add-another"]'),
        pickSr: card.querySelector('[data-kv-pick-sr]'),
        flag: card.querySelector('[data-kv-flag]'),
        flagText: card.querySelector('[data-kv-flag-text]'),
        price: Number(card.dataset.price) || 0,
        rooms: card.dataset.rooms || '|',
      };
      this.products.set(entry.id, entry);
      data.variants.forEach((variant) => {
        this.variants.set(String(variant.id), { product: entry, variant });
      });
    }

    /* ---------- state ---------- */

    restore() {
      try {
        const raw = window.localStorage.getItem(this.storeKey);
        if (!raw) return [];
        const saved = JSON.parse(raw);
        if (!saved || !Array.isArray(saved.v) || Date.now() - Number(saved.t) > STORE_TTL) return [];
        return saved.v
          .map(String)
          .filter((id) => this.variants.has(id))
          .slice(0, this.maxPicks)
          .map((id) => ({ uid: ++this.uidSeq, v: id }));
      } catch (error) {
        return [];
      }
    }

    save() {
      try {
        if (!this.picks.length) window.localStorage.removeItem(this.storeKey);
        else window.localStorage.setItem(this.storeKey, JSON.stringify({ t: Date.now(), v: this.picks.map((p) => p.v) }));
      } catch (error) {
        /* storage unavailable: picks just won't survive a reload */
      }
    }

    selectedVariantId(entry) {
      if (entry.select && this.variants.has(entry.select.value)) return entry.select.value;
      return String(entry.data.variants[0].id);
    }

    countFor(entry) {
      return this.picks.reduce((n, pick) => {
        const ref = this.variants.get(pick.v);
        return ref && ref.product === entry ? n + 1 : n;
      }, 0);
    }

    addPick(entry) {
      if (this.picks.length >= this.maxPicks) {
        this.announce(this.s.msgMax);
        this.flashMessage(this.s.msgMax);
        return;
      }
      this.picks.push({ uid: ++this.uidSeq, v: this.selectedVariantId(entry) });
      this.clearError();
      this.render({ bump: true });
      this.announce(`${fill(this.s.a11yAdded, { title: entry.data.title })} ${this.statusText()}`);
    }

    removeProduct(entry) {
      this.picks = this.picks.filter((pick) => {
        const ref = this.variants.get(pick.v);
        return !(ref && ref.product === entry);
      });
      this.render({ bump: true });
      this.announce(`${fill(this.s.a11yRemoved, { title: entry.data.title })} ${this.statusText()}`);
    }

    removePick(uid) {
      const index = this.picks.findIndex((pick) => String(pick.uid) === String(uid));
      if (index < 0) return;
      const ref = this.variants.get(this.picks[index].v);
      this.picks.splice(index, 1);
      this.render({ bump: true });
      if (ref) this.announce(`${fill(this.s.a11yRemoved, { title: ref.product.data.title })} ${this.statusText()}`);
      // Keep keyboard focus inside the tray after the row disappears.
      const buttons = this.slotList ? this.slotList.querySelectorAll('[data-kv-action="remove"]') : [];
      const next = buttons[index] || buttons[index - 1];
      if (next) next.focus();
      else if (this.trayTitle) this.trayTitle.focus();
    }

    /* ---------- maths (mirrors Shopify BXGY: cheapest items free) ---------- */

    compute() {
      const items = [];
      this.picks.forEach((pick, index) => {
        const ref = this.variants.get(pick.v);
        if (!ref) return;
        items.push({
          uid: pick.uid,
          index,
          price: Number(ref.variant.price) || 0,
          title: ref.product.data.title,
          variantTitle: ref.variant.title,
          image: ref.variant.image || ref.product.data.image,
          url: ref.product.data.url,
        });
      });
      const count = items.length;
      const freeCount = Math.floor(count / SET_SIZE);
      const order = items
        .map((item, i) => ({ i, price: item.price }))
        .sort((a, b) => a.price - b.price || b.i - a.i);
      const freeSet = new Set(order.slice(0, freeCount).map((o) => o.i));
      let subtotal = 0;
      let saving = 0;
      items.forEach((item, i) => {
        subtotal += item.price;
        if (freeSet.has(i)) saving += item.price;
      });
      const remainder = count % SET_SIZE;
      return {
        items,
        count,
        freeCount,
        freeSet,
        subtotal,
        saving,
        total: subtotal - saving,
        remainder,
        need: count < SET_SIZE ? SET_SIZE - count : remainder ? SET_SIZE - remainder : 0,
        target: Math.max(SET_SIZE, Math.ceil(count / SET_SIZE) * SET_SIZE),
      };
    }

    money(cents) {
      return formatMoney(cents, this.cfg.moneyFormat);
    }

    statusText(state = this.compute()) {
      if (!state.count) return this.s.msgStart || '';
      if (!state.freeCount) return fill(this.s.msgMore, { count: state.need });
      let text = fill(this.s.msgReady, {
        items: state.count,
        free: state.freeCount,
        saving: this.money(state.saving),
      });
      if (state.remainder) text += ` ${fill(this.s.msgNext, { count: state.need })}`;
      return text;
    }

    /* ---------- rendering ---------- */

    render(options = {}) {
      const state = this.compute();
      this.renderSlots(state);
      this.renderCards(state);
      this.renderSummary(state);
      this.save();
      if (options.bump && this.bar) {
        this.bar.classList.remove('is-bump');
        // restart the animation
        void this.bar.offsetWidth;
        this.bar.classList.add('is-bump');
      }
    }

    renderSlots(state) {
      if (!this.slotList) return;
      const frag = document.createDocumentFragment();
      for (let i = 0; i < state.target; i += 1) {
        const item = state.items[i];
        const li = el('li', 'kv-b2g1-slot');
        if (!item) {
          li.classList.add('kv-b2g1-slot--empty');
          const num = el('span', 'kv-b2g1-slot__num', String(i + 1));
          num.setAttribute('aria-hidden', 'true');
          li.append(num, el('span', 'kv-b2g1-slot__empty', this.s.emptySlot));
          frag.append(li);
          continue;
        }
        const isFree = state.freeSet.has(i);
        if (isFree) li.classList.add('kv-b2g1-slot--free');

        const thumbWrap = el('span', 'kv-b2g1-slot__thumb');
        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.width = 56;
          img.height = 56;
          img.loading = 'lazy';
          img.decoding = 'async';
          thumbWrap.append(img);
        }

        const info = el('span', 'kv-b2g1-slot__info');
        info.append(el('span', 'kv-b2g1-slot__title', item.title));
        if (item.variantTitle && item.variantTitle !== 'Default Title') {
          info.append(el('span', 'kv-b2g1-slot__variant', item.variantTitle));
        }

        const price = el('span', 'kv-b2g1-slot__price');
        if (isFree) {
          price.append(
            el('span', 'kv-b2g1-slot__free', this.s.freeTag || 'FREE'),
            el('span', 'kv-b2g1-slot__saves', fill(this.s.saves, { amount: this.money(item.price) }))
          );
        } else {
          price.append(el('span', 'kv-b2g1-slot__amount', this.money(item.price)));
        }

        const remove = el('button', 'kv-b2g1-slot__remove');
        remove.type = 'button';
        remove.dataset.kvAction = 'remove';
        remove.dataset.kvUid = String(item.uid);
        remove.setAttribute('aria-label', `${this.s.a11yRemove || 'Remove'}: ${item.title}`);
        remove.innerHTML =
          '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 14 14"><path d="M2 2l10 10M12 2 2 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

        li.append(thumbWrap, info, price, remove);
        frag.append(li);
      }
      this.slotList.replaceChildren(frag);
    }

    renderCards(state) {
      const full = state.count >= this.maxPicks;
      this.products.forEach((entry) => {
        const count = this.countFor(entry);
        const picked = count > 0;
        entry.card.classList.toggle('is-picked', picked);
        if (entry.pickBtn) {
          entry.pickBtn.setAttribute('aria-pressed', picked ? 'true' : 'false');
          entry.pickBtn.setAttribute('aria-disabled', !picked && full ? 'true' : 'false');
        }
        if (entry.pickText) entry.pickText.textContent = picked ? this.s.picked || 'Picked' : this.s.pick || 'Pick';
        if (entry.qtyEl) entry.qtyEl.textContent = count > 1 ? `×${count}` : '';
        // Accessible name starts with the visible label (WCAG 2.5.3) and carries
        // the quantity, which is aria-hidden in its visual "×2" form.
        if (entry.pickSr) entry.pickSr.textContent = count > 1 ? ` (${count}): ${entry.data.title}` : `: ${entry.data.title}`;
        if (entry.moreBtn) {
          entry.moreBtn.hidden = !picked;
          entry.moreBtn.setAttribute('aria-disabled', full ? 'true' : 'false');
        }
        if (entry.flag) {
          entry.flag.hidden = !picked;
          if (entry.flagText) entry.flagText.textContent = count > 1 ? `×${count}` : '';
        }
      });
    }

    renderSummary(state) {
      if (this.msgEl) this.msgEl.textContent = this.statusText(state);

      if (this.totalsEl) this.totalsEl.hidden = state.count === 0;
      if (this.subtotalEl) this.subtotalEl.textContent = this.money(state.subtotal);
      if (this.freeRow) this.freeRow.hidden = state.freeCount === 0;
      if (this.freeLabel) this.freeLabel.textContent = `${this.s.freeRow || ''} (${state.freeCount})`;
      if (this.savingEl) this.savingEl.textContent = `−${this.money(state.saving)}`;
      if (this.totalEl) this.totalEl.textContent = this.money(state.total);

      this.renderShipping(state);

      if (this.progressEl) this.progressEl.textContent = fill(this.s.progress, { items: state.count, target: state.target });
      if (this.miniTotalEl) this.miniTotalEl.textContent = state.count ? this.money(state.total) : '';
      const lit = state.count === 0 ? 0 : state.remainder === 0 ? SET_SIZE : state.remainder;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-on', i < lit));

      if (this.ctaBtn && !this.busy) {
        const ready = state.count >= SET_SIZE;
        this.ctaBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
        this.ctaBtn.classList.toggle('is-ready', ready);
        if (this.ctaText) {
          this.ctaText.textContent = ready
            ? fill(this.s.ctaReady, { items: state.count })
            : fill(this.s.ctaNeed, { count: state.need });
        }
      }
    }

    renderShipping(state = this.compute()) {
      this.renderCartNote(state);
      if (!this.shipEl) return;
      const threshold = Number(this.cfg.freeShipCents) || 0;
      if (!threshold || !state.count) {
        this.shipEl.hidden = true;
        return;
      }
      const combined = state.total + (Number(this.cartTotal) || 0);
      const done = combined >= threshold;
      this.shipEl.hidden = false;
      this.shipEl.classList.toggle('is-done', done);
      this.shipEl.textContent = done
        ? this.s.shipDone
        : fill(this.s.shipMore, { amount: this.money(threshold - combined) });
    }

    // Shopify applies Buy 2 Get 1 across the whole cart, so qualifying items
    // already there change which items end up free. Say so instead of guessing.
    renderCartNote(state) {
      if (!this.cartNoteEl) return;
      const inCart = Number(this.cartQualifying) || 0;
      const show = inCart > 0 && state.count > 0 && Boolean(this.s.cartNote);
      this.cartNoteEl.hidden = !show;
      this.cartNoteEl.textContent = show ? fill(this.s.cartNote, { count: inCart }) : '';
    }

    applyFilters() {
      const bandMax = this.filters.band === 'all' ? Infinity : Number(this.filters.band) * 100;
      const room = this.filters.room;
      let shown = 0;
      this.products.forEach((entry) => {
        const ok = entry.price < bandMax && (room === 'all' || entry.rooms.indexOf(`|${room}|`) !== -1);
        entry.card.hidden = !ok;
        if (ok) shown += 1;
      });
      if (this.countEl) this.countEl.textContent = fill(this.s.count, { count: shown });
      if (this.noResults) this.noResults.hidden = shown > 0;
      this.querySelectorAll('[data-kv-action="filter-band"]').forEach((chip) => {
        chip.setAttribute('aria-pressed', chip.dataset.kvValue === String(this.filters.band) ? 'true' : 'false');
      });
      this.querySelectorAll('[data-kv-action="filter-room"]').forEach((chip) => {
        chip.setAttribute('aria-pressed', chip.dataset.kvValue === String(this.filters.room) ? 'true' : 'false');
      });
    }

    announce(message) {
      if (!this.liveEl || !message) return;
      window.clearTimeout(this.liveTimer);
      this.liveEl.textContent = '';
      this.liveTimer = window.setTimeout(() => {
        this.liveEl.textContent = message;
      }, 60);
    }

    flashMessage(message) {
      if (this.msgEl && message) this.msgEl.textContent = message;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.s.msgError;
      this.errorEl.hidden = false;
      if (this.tray && this.mobileQuery.matches) this.toggleSheet(true, false);
    }

    clearError() {
      if (this.errorEl && !this.errorEl.hidden) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }

    /* ---------- mobile sheet ---------- */

    isSheetOpen() {
      return Boolean(this.tray && this.tray.classList.contains('is-open') && this.mobileQuery.matches);
    }

    // The open sheet sits over a backdrop, so the grid behind it must not take
    // focus or be read out while it is open.
    setBackgroundInert(on) {
      [this.mainEl, this.faqEl].forEach((node) => {
        if (!node) return;
        node.inert = Boolean(on);
        if (on) node.setAttribute('aria-hidden', 'true');
        else node.removeAttribute('aria-hidden');
      });
    }

    toggleSheet(force, moveFocus = true) {
      if (!this.tray) return;
      const open = typeof force === 'boolean' ? force : !this.tray.classList.contains('is-open');
      this.tray.classList.toggle('is-open', open);
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.setBackgroundInert(open && this.mobileQuery.matches);
      if (!moveFocus) return;
      if (open && this.trayTitle) this.trayTitle.focus({ preventScroll: true });
      else if (!open && this.toggleBtn && this.mobileQuery.matches) this.toggleBtn.focus();
    }

    focusableInTray() {
      if (!this.tray) return [];
      return Array.from(
        this.tray.querySelectorAll('a[href], button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((node) => node.getClientRects().length > 0 && !node.closest('[hidden]'));
    }

    trapTab(event) {
      const nodes = this.focusableInTray();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      const outside = !this.tray.contains(active) || active === this.tray || active === this.trayTitle;
      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.tray.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    onTrayFocusOut(event) {
      const next = event.relatedTarget;
      if (!next || !this.isSheetOpen() || this.tray.contains(next)) return;
      this.toggleSheet(false, false);
    }

    onQueryChange() {
      if (!this.mobileQuery.matches && this.tray && this.tray.classList.contains('is-open')) this.toggleSheet(false, false);
      else this.setBackgroundInert(this.isSheetOpen());
    }

    /* ---------- events ---------- */

    onClick(event) {
      const trigger = event.target.closest('[data-kv-action]');
      if (!trigger || !this.contains(trigger)) return;
      const action = trigger.dataset.kvAction;
      const card = trigger.closest('[data-kv-card]');
      const entry = card ? this.products.get(card.dataset.productId) : null;

      switch (action) {
        case 'pick':
          if (!entry) return;
          if (this.countFor(entry) > 0) this.removeProduct(entry);
          else if (trigger.getAttribute('aria-disabled') === 'true') this.announce(this.s.msgMax);
          else this.addPick(entry);
          break;
        case 'add-another':
          if (!entry) return;
          if (trigger.getAttribute('aria-disabled') === 'true') {
            this.announce(this.s.msgMax);
            this.flashMessage(this.s.msgMax);
          } else this.addPick(entry);
          break;
        case 'remove':
          this.removePick(trigger.dataset.kvUid);
          break;
        case 'filter-band':
          this.filters.band = trigger.dataset.kvValue || 'all';
          this.applyFilters();
          break;
        case 'filter-room':
          this.filters.room = trigger.dataset.kvValue || 'all';
          this.applyFilters();
          break;
        case 'reset-filters':
          this.filters = { band: 'all', room: 'all' };
          this.applyFilters();
          break;
        case 'toggle':
          this.toggleSheet();
          break;
        case 'close':
          this.toggleSheet(false);
          break;
        case 'add-to-cart':
          this.addToCart(trigger);
          break;
        default:
      }
    }

    onChange(event) {
      const select = event.target.closest('[data-kv-variant]');
      if (!select) return;
      const card = select.closest('[data-kv-card]');
      if (!card) return;
      card.querySelectorAll('[data-kv-price-for]').forEach((node) => {
        node.hidden = node.dataset.kvPriceFor !== select.value;
      });
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.tray && this.tray.classList.contains('is-open')) {
        event.stopPropagation();
        this.toggleSheet(false);
        return;
      }
      if (event.key === 'Tab' && this.isSheetOpen()) this.trapTab(event);
    }

    /* ---------- cart ---------- */

    routes() {
      const r = window.routes || {};
      return {
        add: `${r.cart_add_url || this.cfg.cartAddUrl || '/cart/add'}.js`,
        cart: r.cart_url || this.cfg.cartUrl || '/cart',
      };
    }

    refreshCartTotal() {
      // Debounced: several cartUpdate events can land in the same tick.
      window.clearTimeout(this.cartTimer);
      this.cartTimer = window.setTimeout(() => {
        fetch(`${this.routes().cart}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((cart) => {
            if (!cart || !this.kvReady) return;
            if (typeof cart.total_price === 'number') this.cartTotal = cart.total_price;
            this.cartQualifying = Array.isArray(cart.items)
              ? cart.items.reduce(
                  (n, item) => (this.products.has(String(item.product_id)) ? n + (Number(item.quantity) || 0) : n),
                  0
                )
              : 0;
            this.renderShipping();
          })
          .catch(() => {});
      }, 120);
    }

    setBusy(busy) {
      this.busy = busy;
      if (!this.ctaBtn) return;
      this.ctaBtn.classList.toggle('is-loading', busy);
      this.ctaBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) {
        this.ctaBtn.setAttribute('aria-disabled', 'true');
        if (this.ctaText) this.ctaText.textContent = this.s.ctaAdding || '…';
      }
    }

    createLinesEvent(items) {
      try {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent || typeof CartLinesUpdateEvent.createPromise !== 'function') return null;
        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
            promise: deferred.promise,
          })
        );
        return deferred;
      } catch (error) {
        return null;
      }
    }

    settleLinesEvent(deferred, error) {
      if (!deferred) return;
      try {
        if (error) {
          deferred.reject(error);
          return;
        }
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        fetch(`${this.routes().cart}.json`)
          .then((response) => response.json())
          .then((cart) => {
            if (!cart || !cart.currency) throw new Error('Missing currency in cart response');
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      } catch (e) {
        /* analytics only */
      }
    }

    addToCart(trigger) {
      const state = this.compute();
      if (this.busy) return;
      if (state.count < SET_SIZE) {
        this.announce(this.statusText(state));
        return;
      }

      const quantities = new Map();
      this.picks.forEach((pick) => {
        if (this.variants.has(pick.v)) quantities.set(pick.v, (quantities.get(pick.v) || 0) + 1);
      });
      const items = Array.from(quantities, ([id, quantity]) => ({ id: Number(id), quantity }));

      const drawer = document.querySelector('cart-drawer');
      const canRenderDrawer =
        drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function';
      const body = { items };
      if (canRenderDrawer) {
        body.sections = drawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(trigger || this.ctaBtn);
      }

      this.clearError();
      this.setBusy(true);
      const linesEvent = this.createLinesEvent(items);

      fetch(this.routes().add, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || (data && data.status)) {
            const message = (data && (data.description || data.message)) || this.s.msgError;
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'kv-b2g1-builder',
                productVariantId: items[0] && items[0].id,
                errors: data && (data.errors || data.description),
                message: data && data.message,
              });
            }
            this.settleLinesEvent(linesEvent, new Error(message));
            this.setBusy(false);
            this.render();
            this.showError(message);
            this.announce(message);
            return;
          }

          this.settleLinesEvent(linesEvent);
          this.picks = [];
          this.setBusy(false);
          this.toggleSheet(false, false);
          this.render();
          this.flashMessage(this.s.msgAdded);
          this.announce(this.s.msgAdded);

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kv-b2g1-builder', cartData: data });
          }

          if (canRenderDrawer && data && data.sections) {
            try {
              drawer.classList.remove('is-empty');
              drawer.renderContents(data);
            } catch (error) {
              console.error('[kv-b2g1] drawer render failed', error);
              window.location = this.routes().cart;
            }
          } else {
            window.location = this.routes().cart;
            return;
          }
          this.refreshCartTotal();
        })
        .catch((error) => {
          console.error('[kv-b2g1]', error);
          this.settleLinesEvent(linesEvent, error);
          this.setBusy(false);
          this.render();
          this.showError(this.s.msgError);
          this.announce(this.s.msgError);
        });
    }
  }

  customElements.define('kv-b2g1-builder', KvB2g1Builder);
})();
